import { gsap, ScrollTrigger } from '../gsap-config.js';
import { initAnimationsForElement } from '../utils/animations.js';

class StatsCounter extends HTMLElement {
  constructor() {
    super();
    this.animatedStats = new WeakSet();
    this.scrollTriggers = [];
  }

  connectedCallback() {
    // Wait for DOM to be ready
    requestAnimationFrame(() => {
      this.initialize();
    });
  }

  disconnectedCallback() {
    // Cleanup ScrollTriggers
    this.scrollTriggers.forEach(st => {
      if (st && st.kill) {
        st.kill();
      }
    });
    this.scrollTriggers = [];
  }

  initialize() {
    const statItems = this.querySelectorAll('.stat-item');
    
    statItems.forEach((item, index) => {
      const valueElement = item.querySelector('.stat-number');
      const statValueElement = item.querySelector('.stat-value');
      
      if (!valueElement || !statValueElement) return;
      
      const targetValue = parseFloat(statValueElement.getAttribute('data-value'));
      const decimals = parseInt(statValueElement.getAttribute('data-decimals') || '0', 10);
      if (isNaN(targetValue)) return;
      
      // Skip if already animated
      if (this.animatedStats.has(item)) return;
      
      // Set initial value
      valueElement.textContent = '0';
      
      // Create ScrollTrigger animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top 80%",
          end: "bottom 20%",
          markers: false,
          onEnter: () => {
            this.animateValue(valueElement, targetValue, statValueElement, decimals);
          },
          once: true // Only trigger once
        }
      });
      
      // Store ScrollTrigger for cleanup
      const st = tl.scrollTrigger;
      if (st) {
        this.scrollTriggers.push(st);
      }
      
      // Mark as animated
      this.animatedStats.add(item);
    });
    
    // Initialize scroll animations for fade-in-up effect
    initAnimationsForElement(this);
  }

  animateValue(element, targetValue, containerElement, decimals = 0) {
    // Skip if already animating
    if (element._isAnimating) return;
    element._isAnimating = true;
    
    const duration = 2; // 2 seconds
    const startTime = performance.now();
    const startValue = 0;
    
    // Check if we need a suffix (%)
    const suffix = containerElement.querySelector('.stat-suffix');
    const hasSuffix = suffix !== null;
    
    // Check if minus sign already exists
    let minusElement = containerElement.querySelector('.stat-minus');
    
    const animate = (currentTime) => {
      const elapsed = (currentTime - startTime) / 1000; // Convert to seconds
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      // Calculate current value (animate from 0 to targetValue)
      const currentValue = startValue + (targetValue - startValue) * easeProgress;
      
      const displayValue = decimals > 0
        ? Math.abs(currentValue).toFixed(decimals)
        : Math.abs(Math.round(currentValue));
      element.textContent = displayValue;
      
      // Handle negative sign
      if (targetValue < 0) {
        // Show minus sign when value is negative
        if (currentValue < -0.5) {
          if (!minusElement) {
            minusElement = document.createElement('span');
            minusElement.className = 'stat-minus';
            minusElement.textContent = '-';
            containerElement.insertBefore(minusElement, element);
          }
        } else {
          // Remove minus sign if value is positive
          if (minusElement) {
            minusElement.remove();
            minusElement = null;
          }
        }
      } else {
        // Remove minus sign if target is positive
        if (minusElement) {
          minusElement.remove();
          minusElement = null;
        }
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete
        element._isAnimating = false;
        // Ensure final value is exact
        const finalValue = decimals > 0
          ? Math.abs(targetValue).toFixed(decimals)
          : Math.abs(Math.round(targetValue));
        element.textContent = finalValue;
        
        // Ensure minus sign is correct for final value
        if (targetValue < 0) {
          if (!minusElement) {
            minusElement = document.createElement('span');
            minusElement.className = 'stat-minus';
            minusElement.textContent = '-';
            containerElement.insertBefore(minusElement, element);
          }
        } else {
          if (minusElement) {
            minusElement.remove();
          }
        }
      }
    };
    
    requestAnimationFrame(animate);
  }
}

customElements.define('stats-counter', StatsCounter);
